import { create } from 'zustand';
import type { Device, Position } from '@/api/types';

/**
 * Atributos que o rastreador manda SÓ no pacote de status, e não no de posição.
 *
 * O GT06 emite dois quadros diferentes: posição (tipo 34), a cada minuto, e status (tipo 19),
 * a cada cinco. O bloqueio viaja só no de status. Sem tratar isso, a embarcação aparece
 * bloqueada por um instante e desbloqueada nos quatro minutos seguintes, piscando entre os
 * dois estados — reportado em campo no "TESTE BARCO2" em 2026-08-11, onde 129 de 896 posições
 * traziam o campo e **todas** diziam bloqueada.
 *
 * Herdar o último valor é seguro porque o decodificador do Traccar grava o campo
 * incondicionalmente a partir do bit de status: quando o veículo é desbloqueado, o próximo
 * pacote de status traz `false` e corrige em até cinco minutos. Ou seja, o valor herdado é
 * sempre o último estado real conhecido, nunca uma invenção.
 *
 * ATENÇÃO: a correção AUTORITATIVA é no servidor. Desde 2026-08-11 o Traccar roda com
 * `processing.copyAttributes=blocked`, que copia o atributo na própria posição antes de
 * gravar — então o dado chega correto ao banco, ao histórico, aos relatórios e ao app, e esta
 * herança aqui praticamente não dispara. Ela fica como rede de segurança para o caso de a
 * configuração ser desligada e para atributos fora daquela lista. Se um dia sobrar só isto, o
 * sintoma volta em tudo que não seja a aba aberta.
 *
 * Só entra aqui atributo que representa ESTADO que permanece, não medição instantânea. Bloqueio
 * permanece até alguém mudar; sinal de rádio e satélites, não. Candidatos ainda não incluídos
 * por falta de evidência de que incomodam: `charge` (energia externa presente) e `batteryLevel`.
 */
const ATRIBUTOS_PERSISTENTES = ['blocked'] as const;

/** Copia para a posição nova o que ela não trouxe e a anterior sabia. */
const herdaEstado = (nova: Position, anterior: Position | undefined): Position => {
  if (!anterior) return nova;
  const faltando = ATRIBUTOS_PERSISTENTES.filter(
    (chave) => nova.attributes?.[chave] === undefined && anterior.attributes?.[chave] !== undefined,
  );
  if (faltando.length === 0) return nova;
  const attributes = { ...(nova.attributes ?? {}) };
  for (const chave of faltando) attributes[chave] = anterior.attributes![chave];
  return { ...nova, attributes };
};

interface FleetState {
  devices: Map<number, Device>;
  positions: Map<number, Position>;
  setDevices: (devices: Device[]) => void;
  setPositions: (positions: Position[]) => void;
  upsertDevices: (devices: Device[]) => void;
  upsertPositions: (positions: Position[]) => void;
  reset: () => void;
}

export const useFleetStore = create<FleetState>((set) => ({
  devices: new Map(),
  positions: new Map(),
  setDevices: (devices) =>
    set(() => ({ devices: new Map(devices.map((d) => [d.id, d])) })),
  setPositions: (positions) =>
    set((state) => {
      const map = new Map<number, Position>();
      for (const p of positions) {
        if (p.deviceId == null) continue;
        const existing = map.get(p.deviceId);
        if (!existing || new Date(p.fixTime) > new Date(existing.fixTime)) {
          // Herda do que já estava na store: recarga da lista não pode apagar o estado
          // que só vem no pacote de status.
          map.set(p.deviceId, herdaEstado(p, existing ?? state.positions.get(p.deviceId)));
        }
      }
      return { positions: map };
    }),
  upsertDevices: (devices) =>
    set((state) => {
      const next = new Map(state.devices);
      for (const d of devices) next.set(d.id, { ...next.get(d.id), ...d });
      return { devices: next };
    }),
  upsertPositions: (positions) =>
    set((state) => {
      const next = new Map(state.positions);
      for (const p of positions) {
        if (p.deviceId == null) continue;
        const existing = next.get(p.deviceId);
        if (!existing || new Date(p.fixTime) >= new Date(existing.fixTime)) {
          next.set(p.deviceId, herdaEstado(p, existing));
        }
      }
      return { positions: next };
    }),
  reset: () => set({ devices: new Map(), positions: new Map() }),
}));
